import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import moment from 'moment';
import Checkbox from 'material-ui/Checkbox';
import CircularProgress from 'material-ui/CircularProgress';
import DatePicker from 'material-ui/DatePicker';
import Dialog from 'material-ui/Dialog';
import FlatButton from 'material-ui/FlatButton';
import MenuItem from 'material-ui/MenuItem';
import RaisedButton from 'material-ui/RaisedButton';
import SelectField from 'material-ui/SelectField';
import Slider from 'material-ui/Slider';
import Snackbar from 'material-ui/Snackbar';
import TextField from 'material-ui/TextField';
import Toggle from 'material-ui/Toggle';
import { Tab, Tabs } from 'material-ui/Tabs';
import {
    Table, TableBody, TableHeader, TableHeaderColumn, TableRow, TableRowColumn,
} from 'material-ui/Table';

import api from 'services';
import PortsAutoComplete from '../common/PortsAutoComplete';
import CongestionHistogram from './CongestionHistogram';
import CongestionMap from './CongestionMap';
import './styles.scss';

const CELL_STATE_OPTIONS = ['candidate', 'confirmed', 'rejected'];
const ZONE_TYPE_OPTIONS = ['anchorage', 'port'];
const ACTIVE_RUN_STATUSES = ['queued', 'running'];

function flattenErrorMessage(value) {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
        return value.map(flattenErrorMessage).filter(Boolean).join('; ');
    }
    if (typeof value === 'object') {
        if (value.constraints) return Object.keys(value.constraints).map(key => value.constraints[key]).join('; ');
        if (value.message) return flattenErrorMessage(value.message);
        if (value.messages) return flattenErrorMessage(value.messages);
    }
    return '';
}

function mutationError(error, fallback) {
    const body = error && error.response && error.response.data;
    if (!body) return fallback;
    return flattenErrorMessage(body.message) || flattenErrorMessage(body.errors) || fallback;
}

function idsForAddresses(features, selectedAddresses) {
    const selected = {};
    (selectedAddresses || []).forEach((address) => { selected[String(address)] = true; });
    const ids = [];
    features.forEach((feature) => {
        const props = feature.properties || {};
        if (!selected[String(props.cellAddress || '')]) return;
        const id = props._id != null ? String(props._id) : (feature.id != null ? String(feature.id) : '');
        if (id) ids.push(id);
    });
    return ids;
}

function zoneFeatureId(feature) {
    const props = (feature && feature.properties) || {};
    if (props._id != null) return String(props._id);
    if (feature && feature.id != null) return String(feature.id);
    return '';
}

const fmtUtc = value => (value ? moment.utc(value).format('YYYY-MM-DD HH:mm') : '—');

const NAV_STATUS_LABELS = { 0: 'Under way', 1: 'At anchor', 5: 'Moored' };

function navStatusLabel(status) {
    if (typeof status !== 'number') return '—';
    // return NAV_STATUS_LABELS[status] ? `${status} ${NAV_STATUS_LABELS[status]}` : String(status);
    return `Status:${status}`;
}

/** The stored point the slider is sitting on: the last one whose start is at or before `ts`. */
function pointAt(points, ts) {
    let index = 0;
    while (index < points.length - 1 && new Date(points[index + 1].ts).getTime() <= ts) index += 1;
    return points[index];
}

const TRACK_EVENT_LABELS = {
    entered_anchorage: 'Entered anchorage',
    entered_port: 'Entered port',
};

/** Where one event sits on the slider, as a percentage of the track's time span. */
function scrubPercent(ts, range) {
    const span = range[1] - range[0];
    if (span <= 0) return 0;
    return Math.min(100, Math.max(0, ((ts - range[0]) / span) * 100));
}

function formatBytes(value) {
    const bytes = Number(value);
    if (!Number.isFinite(bytes) || bytes < 0) return '—';
    if (bytes < 1024) return `${Math.round(bytes)} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
const minutesToHours = value => (value === null || value === undefined ? '—' : (value / 60).toFixed(1));

function MetricsRow({ label, metrics }) {
    return (
        <TableRow>
            <TableRowColumn>{label}</TableRowColumn>
            <TableRowColumn className='num'>{metrics.completedVisits}</TableRowColumn>
            <TableRowColumn className='num'>{metrics.directEntries}</TableRowColumn>
            <TableRowColumn className='num'>{metrics.unresolved}</TableRowColumn>
            <TableRowColumn className='num'>{minutesToHours(metrics.avg)}</TableRowColumn>
            <TableRowColumn className='num'>{minutesToHours(metrics.median)}</TableRowColumn>
            <TableRowColumn className='num'>{minutesToHours(metrics.min)}</TableRowColumn>
            <TableRowColumn className='num'>{minutesToHours(metrics.max)}</TableRowColumn>
        </TableRow>
    );
}

export default function Congestion() {
    const queryClient = useQueryClient();

    const [port, setPort] = useState(null);
    const [dateFrom, setDateFrom] = useState(moment.utc().month(4).date(1).startOf('day').toDate());
    const [dateTo, setDateTo] = useState(moment.utc().endOf('day').toDate());
    // each box starts on; an empty selection shows nothing
    const [cellStates, setCellStates] = useState(CELL_STATE_OPTIONS.slice());
    const [zoneTypes, setZoneTypes] = useState(ZONE_TYPE_OPTIONS.slice());
    const [newZoneType, setNewZoneType] = useState(ZONE_TYPE_OPTIONS[0]);
    const [selectedZoneIds, setSelectedZoneIds] = useState([]);
    const [zoneNameDrafts, setZoneNameDrafts] = useState({});
    const [scanMode, setScanMode] = useState('radius');
    const [radiusNm, setRadiusNm] = useState('30');
    const [selectedAddresses, setSelectedAddresses] = useState([]);
    const [drawEnabled, setDrawEnabled] = useState(false);
    const [showTerminals, setShowTerminals] = useState(false);
    const [tab, setTab] = useState('zones');
    const [trackMmsi, setTrackMmsi] = useState(null);
    const [scrubTs, setScrubTs] = useState(null);
    const [message, setMessage] = useState('');
    const [scanEstimate, setScanEstimate] = useState(null);

    const portId = port && (port._id || port.id);
    const range = useMemo(() => ({
        dateFrom: moment.utc(dateFrom).toISOString(),
        dateTo: moment.utc(dateTo).toISOString(),
    }), [dateFrom, dateTo]);

    const portInfo = useQuery(['congestionPort', portId],
        () => api.getCongestionPort(portId), { enabled: !!portId });

    const cells = useQuery(['congestionCells', portId],
        () => api.getCongestionHexCells(portId, { limit: 5000 }), { enabled: !!portId });

    const toggleCellState = (state) => {
        setCellStates(current => (
            current.indexOf(state) === -1
                ? current.concat(state)
                : current.filter(item => item !== state)
        ));
    };

    const toggleZoneType = (type) => {
        setZoneTypes(current => (
            current.indexOf(type) === -1
                ? current.concat(type)
                : current.filter(item => item !== type)
        ));
    };

    const zones = useQuery(['congestionZones', portId],
        () => api.getCongestionZones(portId), { enabled: !!portId });

    const runs = useQuery(['congestionRuns', portId],
        () => api.getCongestionRuns(portId, { limit: 20 }), {
            enabled: !!portId,
            refetchInterval: data => (
                data && (data.runs || []).some(run => ACTIVE_RUN_STATUSES.indexOf(run.status) !== -1) ? 5000 : false
            ),
        });

    const stats = useQuery(['congestionStats', portId, range.dateFrom, range.dateTo],
        () => api.getCongestionStats(portId, {
            ...range, includeVisits: true, visitLimit: 200,
        }), { enabled: !!portId });

    const track = useQuery(['congestionTrack', trackMmsi, portId, range.dateFrom, range.dateTo],
        () => api.getCongestionVesselTrack(trackMmsi, { portId, ...range }), {
            enabled: !!portId && !!trackMmsi,
            onSuccess: (data) => {
                const first = data && data.points && data.points[0];
                setScrubTs(first ? new Date(first.ts).getTime() : null);
            },
        });

    const refreshCells = () => {
        queryClient.invalidateQueries('congestionCells');
        queryClient.invalidateQueries('congestionZones');
    };

    const selectedCellIds = () => {
        const features = (cells.data && cells.data.featureCollection && cells.data.featureCollection.features) || [];
        return idsForAddresses(features, selectedAddresses);
    };

    const confirmCells = useMutation(() => api.confirmCongestionHexCells(portId, selectedAddresses, selectedCellIds()), {
        onSuccess: (result) => {
            const confirmed = (result && result.confirmed) || selectedAddresses.length;
            const skipped = result && result.skipped;
            setMessage(skipped
                ? `Confirmed ${confirmed} cells; ${skipped} empty cells need a zone or a neighbouring cell`
                : `Confirmed ${confirmed} cells`);
            setSelectedAddresses([]);
            refreshCells();
        },
        onError: error => setMessage(mutationError(error, 'Confirm failed')),
    });

    const rejectCells = useMutation(() => api.rejectCongestionHexCells(portId, selectedAddresses, selectedCellIds()), {
        onSuccess: (result) => {
            setMessage(`Rejected ${(result && result.rejected) || selectedAddresses.length} cells`);
            setSelectedAddresses([]);
            refreshCells();
        },
        onError: error => setMessage(mutationError(error, 'Reject failed')),
    });

    const deleteCells = useMutation(() => api.deleteCongestionHexCells(portId, selectedAddresses, selectedCellIds()), {
        onSuccess: (result) => {
            setMessage(`Deleted ${(result && result.deleted) || selectedAddresses.length} cells`);
            setSelectedAddresses([]);
            refreshCells();
        },
        onError: error => setMessage(mutationError(error, 'Delete failed')),
    });

    const addCellsToZone = useMutation(
        () => api.addCongestionCellsToZone(portId, selectedZoneIds[0], selectedAddresses, selectedCellIds()),
        {
            onSuccess: (result) => {
                setMessage(`Added ${(result && result.added) || selectedAddresses.length} cells to the zone`);
                setSelectedAddresses([]);
                refreshCells();
            },
            onError: error => setMessage(mutationError(error, 'Failed to add cells to the zone')),
        },
    );

    const removeCellsFromZone = useMutation(
        () => api.removeCongestionCellsFromZone(portId, selectedZoneIds[0], selectedAddresses, selectedCellIds()),
        {
            onSuccess: (result) => {
                setMessage(`Removed ${(result && result.rejected) || selectedAddresses.length} cells from the zone`);
                setSelectedAddresses([]);
                refreshCells();
            },
            onError: error => setMessage(mutationError(error, 'Failed to remove cells from the zone')),
        },
    );

    const mergeZones = useMutation(
        () => api.mergeCongestionZones(portId, selectedZoneIds),
        {
            onSuccess: (zone) => {
                const name = zone && zone.name ? zone.name : 'zone';
                setMessage(`Merged ${selectedZoneIds.length} zones into ${name}`);
                setSelectedZoneIds(zone && zone._id ? [String(zone._id)] : []);
                refreshCells();
            },
            onError: error => setMessage(mutationError(error, 'Failed to merge zones')),
        },
    );

    const createZone = useMutation(async () => {
        const zone = await api.createCongestionZone(portId, newZoneType);
        const id = zone && String(zone._id || zone.id || '');
        let added = 0;
        let addError = '';
        if (id && selectedAddresses.length) {
            try {
                const result = await api.addCongestionCellsToZone(portId, id, selectedAddresses, selectedCellIds());
                added = (result && result.added) || selectedAddresses.length;
            } catch (error) {
                addError = mutationError(error, 'Zone created, but the selected cells could not be added');
            }
        }
        return { id, added, addError };
    }, {
        onSuccess: ({ id, added, addError }) => {
            if (id) setSelectedZoneIds([id]);
            if (added) setSelectedAddresses([]);
            if (addError) {
                setMessage(addError);
            } else if (added) {
                setMessage(`Created ${newZoneType} zone and added ${added} cells`);
            } else {
                setMessage(`Created ${newZoneType} zone`);
            }
            refreshCells();
        },
        onError: error => setMessage(mutationError(error, 'Failed to create the zone')),
    });

    const saveZoneNames = useMutation(async () => {
        const dirty = zoneRows.filter(zone => (zoneNameDrafts[zone.id] || '') !== (zone.name || ''));
        await Promise.all(dirty.map(zone => api.renameCongestionZone(portId, zone.id, zoneNameDrafts[zone.id] || '')));
        return dirty.length;
    }, {
        onSuccess: (count) => {
            setMessage(count === 1 ? 'Saved zone name' : `Saved ${count} zone names`);
            queryClient.invalidateQueries('congestionZones');
        },
        onError: error => setMessage(mutationError(error, 'Failed to save zone names')),
    });

    const scanBody = () => ({
        ...range,
        mode: scanMode,
        radiusNm: scanMode === 'radius' ? Number(radiusNm) || undefined : undefined,
    });

    const estimateScan = useMutation(() => api.estimateCongestionScan(portId, scanBody()), {
        onSuccess: (estimate) => {
            setScanEstimate(estimate);
        },
        onError: error => setMessage(mutationError(error, 'Failed to estimate the scan')),
    });

    const startScan = useMutation(() => api.startCongestionScan(portId, scanBody()), {
        onSuccess: (run) => {
            setScanEstimate(null);
            setMessage(`Scan started (${(run && run.status) || 'running'})`);
            queryClient.invalidateQueries('congestionRuns');
        },
        onError: error => setMessage(mutationError(error, 'Failed to start the scan')),
    });

    const startCalculate = useMutation(() => api.startCongestionCalculate(portId, range), {
        onSuccess: () => {
            setMessage('Calculate started; statistics will refresh when it finishes');
            queryClient.invalidateQueries('congestionRuns');
        },
        onError: () => setMessage('Failed to start calculate'),
    });

    const hadActiveRun = useRef(false);
    useEffect(() => {
        const list = (runs.data && runs.data.runs) || [];
        const active = list.some(run => ACTIVE_RUN_STATUSES.indexOf(run.status) !== -1);
        if (hadActiveRun.current && !active) {
            queryClient.invalidateQueries('congestionCells');
            queryClient.invalidateQueries('congestionZones');
            queryClient.invalidateQueries('congestionStats');
        }
        hadActiveRun.current = active;
    }, [queryClient, runs.data]);

    const toggleZone = (zoneId) => {
        if (!zoneId) return;
        setSelectedZoneIds(current => (
            current.indexOf(zoneId) === -1
                ? current.concat(zoneId)
                : current.filter(item => item !== zoneId)
        ));
    };

    const cellsFeatures = useMemo(() => {
        const features = (cells.data && cells.data.featureCollection && cells.data.featureCollection.features) || [];
        if (!cellStates.length) return [];
        return features.filter(feature => cellStates.indexOf((feature.properties || {}).state) !== -1);
    }, [cells.data, cellStates]);

    const selectedMmsis = useMemo(() => {
        if (!selectedAddresses.length) return [];
        const selected = {};
        selectedAddresses.forEach((address) => { selected[String(address)] = true; });
        const features = (cells.data && cells.data.featureCollection && cells.data.featureCollection.features) || [];
        const mmsis = {};
        features.forEach((feature) => {
            const props = feature.properties || {};
            const address = props.cellAddress != null ? String(props.cellAddress) : '';
            if (!selected[address]) return;
            (props.mmsiList || []).forEach((mmsi) => { mmsis[mmsi] = true; });
        });
        return Object.keys(mmsis).map(Number).sort((a, b) => a - b);
    }, [cells.data, selectedAddresses]);
    const allZoneFeatures = useMemo(
        () => (zones.data && zones.data.featureCollection && zones.data.featureCollection.features) || [],
        [zones.data],
    );
    const zoneRows = useMemo(() => allZoneFeatures.map((feature) => {
        const props = feature.properties || {};
        const id = zoneFeatureId(feature);
        return {
            id,
            name: props.name || '',
            zoneType: props.zoneType,
            navStatus: props.navStatus,
            cellCount: props.cellCount || 0,
        };
    }), [allZoneFeatures]);
    const dirtyZoneNames = zoneRows.some(zone => (zoneNameDrafts[zone.id] || '') !== (zone.name || ''));
    const visibleZoneRows = zoneTypes.length
        ? zoneRows.filter(zone => zoneTypes.indexOf(zone.zoneType) !== -1)
        : [];
    const zonesFeatures = useMemo(() => {
        if (!zoneTypes.length) return [];
        return allZoneFeatures.filter((feature) => {
            const props = feature.properties || {};
            return zoneTypes.indexOf(props.zoneType) !== -1 && feature.geometry;
        });
    }, [allZoneFeatures, zoneTypes]);

    useEffect(() => {
        setZoneNameDrafts((current) => {
            const next = {};
            zoneRows.forEach((zone) => {
                next[zone.id] = Object.prototype.hasOwnProperty.call(current, zone.id)
                    ? current[zone.id]
                    : zone.name;
            });
            return next;
        });
        setSelectedZoneIds((current) => {
            const next = current.filter(id => zoneRows.some(zone => zone.id === id));
            if (next.length === current.length && next.every((id, index) => id === current[index])) {
                return current;
            }
            return next;
        });
    }, [zoneRows]);
    const summary = stats.data && stats.data.summary;
    const byVesselType = (stats.data && stats.data.byVesselType) || [];
    const visits = (stats.data && stats.data.visits) || [];
    const runRows = (runs.data && runs.data.runs) || [];
    const latestScan = runRows.find(run => run.type === 'scan');
    const scanRadiusNm = latestScan && latestScan.params
        && latestScan.params.mode === 'radius'
        && latestScan.params.radiusNm > 0
        ? latestScan.params.radiusNm
        : null;
    const selectionRadiusNm = scanRadiusNm
        || (scanMode === 'radius' && Number(radiusNm) > 0 ? Number(radiusNm) : null);
    const singleZoneId = selectedZoneIds.length === 1 ? selectedZoneIds[0] : null;
    const selectedZoneRows = zoneRows.filter(zone => selectedZoneIds.indexOf(zone.id) !== -1);
    const canMergeZones = selectedZoneRows.length >= 2
        && selectedZoneRows.every(zone => zone.zoneType === selectedZoneRows[0].zoneType);
    const trackPoints = (track.data && track.data.points) || [];
    const trackRange = trackPoints.length
        ? [new Date(trackPoints[0].ts).getTime(), new Date(trackPoints[trackPoints.length - 1].ts).getTime()]
        : null;
    const scrubValue = trackRange
        ? Math.min(Math.max(scrubTs || trackRange[0], trackRange[0]), trackRange[1])
        : null;
    const scrubPoint = scrubValue ? pointAt(trackPoints, scrubValue) : null;
    const scrubEvents = trackRange
        ? ((track.data && track.data.events) || [])
            .filter(event => TRACK_EVENT_LABELS[event.type])
            .map(event => ({ type: event.type, ts: new Date(event.ts).getTime() }))
            .filter(event => event.ts >= trackRange[0] && event.ts <= trackRange[1])
        : [];
    const busy = cells.isFetching || zones.isFetching || stats.isFetching;

    return (
        <div className='congestion-page container-fluid'>
            <h2>Port Congestion</h2>

            <p className='congestion-guide'>
                <b>Scan</b> reads AIS for the selected port and dates, inside the radius or the port polygon, and stores tracks and candidate cells. It does not compute waiting times.
                {' '}
                <b>Calculate</b> rebuilds waiting times from the latest completed scan(s) and from the cells and zones as they are now. Dates outside that total scan range are dropped.
                Statistics on this page always come from the latest completed calculate; the date filters only narrow that run.
                A later scan or calculate over a shorter range replaces the wider one as what you see, so run both for the full period you want to keep.
                Note that for example, we run a monthly scan (for the previous month each time) for all ports, and right after that we run a calculate for all previous scans including the (current) last one.
            </p>

            <div className='congestion-filters'>
                <PortsAutoComplete
                    hintText='Search port…'
                    floatingLabelText='Port'
                    selectValue={selected => {
                        setPort(selected);
                        setSelectedAddresses([]);
                        setSelectedZoneIds([]);
                        setZoneNameDrafts({});
                        setScanEstimate(null);
                        setTrackMmsi(null);
                    }}
                />
                <DatePicker
                    floatingLabelText='Date from (UTC)'
                    value={dateFrom}
                    onChange={(event, value) => setDateFrom(value)}
                    autoOk
                    container='inline'
                />
                <DatePicker
                    floatingLabelText='Date to (UTC)'
                    value={dateTo}
                    onChange={(event, value) => setDateTo(value)}
                    autoOk
                    container='inline'
                />
                <div className='congestion-cellstates'>
                    <span className='congestion-cellstates-label'>Cells:</span>
                    {CELL_STATE_OPTIONS.map(state => (
                        <Checkbox
                            key={state}
                            label={state}
                            checked={cellStates.indexOf(state) !== -1}
                            onCheck={() => toggleCellState(state)}
                            style={{ width: 'auto' }}
                        />
                    ))}
                </div>
                <div className='congestion-cellstates'>
                    <span className='congestion-cellstates-label'>Zones:</span>
                    {ZONE_TYPE_OPTIONS.map(type => (
                        <Checkbox
                            key={type}
                            label={type}
                            checked={zoneTypes.indexOf(type) !== -1}
                            onCheck={() => toggleZoneType(type)}
                            style={{ width: 'auto' }}
                        />
                    ))}
                </div>
                {busy && <CircularProgress size={24} className='congestion-progress' />}
            </div>

            <div className='congestion-filters congestion-actions'>
                <SelectField
                    floatingLabelText='Scan mode'
                    value={scanMode}
                    onChange={(event, index, value) => setScanMode(value)}
                    style={{ width: 130 }}
                >
                    <MenuItem value='radius' primaryText='radius' />
                    <MenuItem value='polygon' primaryText='polygon' />
                </SelectField>
                {scanMode === 'radius' && (
                    <TextField
                        floatingLabelText='Radius, nm'
                        value={radiusNm}
                        onChange={event => setRadiusNm(event.target.value)}
                        style={{ width: 100 }}
                    />
                )}
                <RaisedButton
                    label={estimateScan.isLoading ? 'Estimating…' : 'Scan'}
                    primary
                    disabled={!portId || estimateScan.isLoading || startScan.isLoading}
                    onClick={() => estimateScan.mutate()}
                />
                <RaisedButton
                    label='Calculate'
                    disabled={!portId || startCalculate.isLoading}
                    onClick={() => startCalculate.mutate()}
                />
                <span className='congestion-spacer' />
                <Toggle
                    label='Terminals'
                    labelPosition='right'
                    toggled={showTerminals}
                    onToggle={(event, checked) => setShowTerminals(checked)}
                    className='congestion-draw-toggle'
                />
                <Toggle
                    label='Draw'
                    labelPosition='right'
                    toggled={drawEnabled}
                    onToggle={(event, checked) => setDrawEnabled(checked)}
                    className='congestion-draw-toggle'
                />
                <span className='congestion-selection'>{selectedAddresses.length} cells selected</span>
                <RaisedButton
                    label='Confirm'
                    primary
                    disabled={!selectedAddresses.length || confirmCells.isLoading}
                    onClick={() => confirmCells.mutate()}
                />
                <FlatButton
                    label='Reject'
                    secondary
                    disabled={!selectedAddresses.length || rejectCells.isLoading}
                    onClick={() => rejectCells.mutate()}
                />
                <FlatButton
                    label='Delete'
                    secondary
                    disabled={!selectedAddresses.length || deleteCells.isLoading}
                    onClick={() => deleteCells.mutate()}
                />
                <FlatButton
                    label='Clear'
                    disabled={!selectedAddresses.length}
                    onClick={() => setSelectedAddresses([])}
                />
                <RaisedButton
                    label='Add to zone'
                    disabled={!singleZoneId || !selectedAddresses.length || addCellsToZone.isLoading}
                    onClick={() => addCellsToZone.mutate()}
                />
                <FlatButton
                    label='Remove from zone'
                    secondary
                    disabled={!singleZoneId || !selectedAddresses.length || removeCellsFromZone.isLoading}
                    onClick={() => removeCellsFromZone.mutate()}
                />
            </div>

            <div className='congestion-split'>
                <div className='congestion-split-map'>
                    <CongestionMap
                        portInfo={portInfo.data}
                        selectionRadiusNm={selectionRadiusNm}
                        cellsFeatures={cellsFeatures}
                        zonesFeatures={zonesFeatures}
                        selectedAddresses={selectedAddresses}
                        selectedZoneIds={selectedZoneIds}
                        drawEnabled={drawEnabled}
                        showTerminals={showTerminals}
                        onSelectionChange={setSelectedAddresses}
                        track={track.data}
                        scrubTs={scrubTs}
                    />
                </div>

                <Tabs value={tab} onChange={setTab} className='congestion-tabs congestion-split-panel'>
                    <Tab label={`Zones (${visibleZoneRows.length})`} value='zones'>
                        <div className='congestion-tab-body congestion-tab-body-zones'>
                            <div className='congestion-zones'>
                                <div className='congestion-zones-head'>
                                    <SelectField
                                        floatingLabelText='Zone type'
                                        value={newZoneType}
                                        onChange={(event, index, value) => setNewZoneType(value)}
                                        style={{ width: 120 }}
                                    >
                                        {ZONE_TYPE_OPTIONS.map(type => (
                                            <MenuItem key={type} value={type} primaryText={type} />
                                        ))}
                                    </SelectField>
                                    <RaisedButton
                                        label={createZone.isLoading ? 'Creating…' : 'Create zone'}
                                        disabled={!portId || createZone.isLoading}
                                        onClick={() => createZone.mutate()}
                                    />
                                    <RaisedButton
                                        label='Merge'
                                        disabled={!canMergeZones || mergeZones.isLoading}
                                        onClick={() => mergeZones.mutate()}
                                    />
                                    <RaisedButton
                                        label='Save names'
                                        primary
                                        disabled={!dirtyZoneNames || saveZoneNames.isLoading}
                                        onClick={() => saveZoneNames.mutate()}
                                    />
                                    <span className='congestion-selection'>
                                        {selectedZoneIds.length
                                            ? `${selectedZoneIds.length} selected`
                                            : 'click a row to select · add/remove needs exactly one'}
                                    </span>
                                </div>
                                {!visibleZoneRows.length && (
                                    <p className='congestion-empty'>
                                        {zoneRows.length
                                            ? 'No zones of the selected types.'
                                            : 'No zones yet. Create one, or confirm cells to create them, then name them here.'}
                                    </p>
                                )}
                                {!!visibleZoneRows.length && (
                                    <div className='congestion-zone-table-wrap'>
                                        <table className='congestion-zone-table'>
                                            <thead>
                                                <tr>
                                                    <th className='congestion-zone-check' />
                                                    <th>Type</th>
                                                    <th>Name</th>
                                                    <th className='num'>Cells</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {visibleZoneRows.map(zone => (
                                                    <tr
                                                        key={zone.id}
                                                        className={selectedZoneIds.indexOf(zone.id) !== -1 ? 'is-selected' : ''}
                                                        onClick={() => toggleZone(zone.id)}
                                                    >
                                                        <td className='congestion-zone-check'>
                                                            <input
                                                                type='checkbox'
                                                                checked={selectedZoneIds.indexOf(zone.id) !== -1}
                                                                onChange={() => {}}
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    toggleZone(zone.id);
                                                                }}
                                                            />
                                                        </td>
                                                        <td>
                                                            <span className={`congestion-zone-type congestion-zone-type-${zone.zoneType}`}>
                                                                {zone.zoneType}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <TextField
                                                                name={`zone-name-${zone.id}`}
                                                                hintText='Zone name'
                                                                value={zoneNameDrafts[zone.id] != null ? zoneNameDrafts[zone.id] : zone.name}
                                                                onChange={(event) => {
                                                                    const value = event.target.value;
                                                                    setZoneNameDrafts(current => ({ ...current, [zone.id]: value }));
                                                                }}
                                                                onClick={event => event.stopPropagation()}
                                                                style={{ width: '100%' }}
                                                                underlineShow
                                                            />
                                                        </td>
                                                        <td className='num congestion-zone-meta'>{zone.cellCount}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Tab>
                    <Tab label='Statistics' value='stats'>
                        <div className='congestion-tab-body'>
                            {!summary && <p className='congestion-empty'>Pick a port and run calculate to see congestion statistics.</p>}
                            {summary && (
                                <Table selectable={false}>
                                    <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
                                        <TableRow>
                                            <TableHeaderColumn>Vessel type</TableHeaderColumn>
                                            <TableHeaderColumn className='num'>Completed</TableHeaderColumn>
                                            <TableHeaderColumn className='num'>Direct</TableHeaderColumn>
                                            <TableHeaderColumn className='num'>Unresolved</TableHeaderColumn>
                                            <TableHeaderColumn className='num'>Avg, h</TableHeaderColumn>
                                            <TableHeaderColumn className='num'>Median, h</TableHeaderColumn>
                                            <TableHeaderColumn className='num'>Min, h</TableHeaderColumn>
                                            <TableHeaderColumn className='num'>Max, h</TableHeaderColumn>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody displayRowCheckbox={false}>
                                        <MetricsRow label='All vessels' metrics={summary} />
                                        {byVesselType.map(row => (
                                            <MetricsRow key={row.vesselType} label={row.vesselType} metrics={row} />
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </div>
                    </Tab>
                    <Tab label={`Visits (${visits.length})`} value='visits'>
                        <div className='congestion-tab-body'>
                            {!visits.length && <p className='congestion-empty'>No visits in the selected period.</p>}
                            {!!visits.length && (
                                <Table selectable={false}>
                                    <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
                                        <TableRow>
                                            <TableHeaderColumn>Vessel</TableHeaderColumn>
                                            <TableHeaderColumn>MMSI</TableHeaderColumn>
                                            <TableHeaderColumn>Type</TableHeaderColumn>
                                            <TableHeaderColumn>Anchorage entered</TableHeaderColumn>
                                            <TableHeaderColumn>Port entered</TableHeaderColumn>
                                            <TableHeaderColumn className='num'>Waiting, h</TableHeaderColumn>
                                            <TableHeaderColumn>Status</TableHeaderColumn>
                                            <TableHeaderColumn>Track</TableHeaderColumn>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody displayRowCheckbox={false}>
                                        {visits.map(visit => (
                                            <TableRow key={visit._id || `${visit.mmsi}-${visit.anchorageEnteredAt}`}>
                                                <TableRowColumn>{visit.vesselName || '—'}</TableRowColumn>
                                                <TableRowColumn>{visit.mmsi}</TableRowColumn>
                                                <TableRowColumn>{visit.vesselType || 'unknown'}</TableRowColumn>
                                                <TableRowColumn>{fmtUtc(visit.anchorageEnteredAt)}</TableRowColumn>
                                                <TableRowColumn>{fmtUtc(visit.portEnteredAt)}</TableRowColumn>
                                                <TableRowColumn className='num'>{minutesToHours(visit.waitingTimeMinutes)}</TableRowColumn>
                                                <TableRowColumn>{visit.status}</TableRowColumn>
                                                <TableRowColumn>
                                                    <FlatButton
                                                        label='Show'
                                                        primary
                                                        onClick={() => { setTrackMmsi(visit.mmsi); }}
                                                    />
                                                </TableRowColumn>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </div>
                    </Tab>
                    <Tab label={`Runs (${runRows.length})`} value='runs'>
                        <div className='congestion-tab-body'>
                            {!runRows.length && <p className='congestion-empty'>No scan or calculate runs for this port yet.</p>}
                            {!!runRows.length && (
                                <Table selectable={false}>
                                    <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
                                        <TableRow>
                                            <TableHeaderColumn>Type</TableHeaderColumn>
                                            <TableHeaderColumn>Status</TableHeaderColumn>
                                            <TableHeaderColumn>Period</TableHeaderColumn>
                                            <TableHeaderColumn>Started</TableHeaderColumn>
                                            <TableHeaderColumn>Finished</TableHeaderColumn>
                                            <TableHeaderColumn>Error</TableHeaderColumn>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody displayRowCheckbox={false}>
                                        {runRows.map(run => (
                                            <TableRow key={run._id} className={`congestion-run-${run.status}`}>
                                                <TableRowColumn>{run.type}</TableRowColumn>
                                                <TableRowColumn>{run.status}</TableRowColumn>
                                                <TableRowColumn>
                                                    {run.params ? `${fmtUtc(run.params.dateFrom)} — ${fmtUtc(run.params.dateTo)}` : '—'}
                                                </TableRowColumn>
                                                <TableRowColumn>{fmtUtc(run.startedAt)}</TableRowColumn>
                                                <TableRowColumn>{fmtUtc(run.finishedAt)}</TableRowColumn>
                                                <TableRowColumn>{run.error || '—'}</TableRowColumn>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </div>
                    </Tab>
                </Tabs>
            </div>

            {trackRange && (
                <div className='congestion-scrub'>
                    <span className='congestion-scrub-edge'>{fmtUtc(trackRange[0])}</span>
                    <div className='congestion-scrub-slider'>
                        <Slider
                            min={trackRange[0]}
                            max={trackRange[1]}
                            value={scrubValue}
                            onChange={(event, value) => setScrubTs(value)}
                            sliderStyle={{ margin: 0 }}
                        />
                        <div className='congestion-scrub-marks'>
                            {scrubEvents.map(event => (
                                <button
                                    key={`${event.type}-${event.ts}`}
                                    type='button'
                                    className={`congestion-scrub-mark congestion-scrub-mark-${event.type}`}
                                    style={{ left: `${scrubPercent(event.ts, trackRange)}%` }}
                                    title={`${TRACK_EVENT_LABELS[event.type]} · ${fmtUtc(event.ts)} UTC`}
                                    onClick={() => setScrubTs(event.ts)}
                                />
                            ))}
                        </div>
                    </div>
                    <span className='congestion-scrub-edge'>{fmtUtc(trackRange[1])}</span>
                    <span className='congestion-scrub-value'>
                        {fmtUtc(scrubValue)} UTC &middot; MMSI {trackMmsi}
                        {' '}&middot; {navStatusLabel(scrubPoint && scrubPoint.navStatus)}
                    </span>
                    <FlatButton label='Close track' onClick={() => { setTrackMmsi(null); setScrubTs(null); }} />
                </div>
            )}

            {!!selectedAddresses.length && (
                <div className='congestion-cell-mmsis'>
                    <span className='congestion-cell-mmsis-label'>
                        MMSIs in {selectedAddresses.length} selected cell{selectedAddresses.length === 1 ? '' : 's'}
                        {selectedMmsis.length ? ` (${selectedMmsis.length})` : ''}:
                    </span>
                    {selectedMmsis.length
                        ? selectedMmsis.map(mmsi => (
                            <button
                                key={mmsi}
                                type='button'
                                className={`congestion-cell-mmsi${String(trackMmsi) === String(mmsi) ? ' is-active' : ''}`}
                                onClick={() => setTrackMmsi(mmsi)}
                            >
                                {mmsi}
                            </button>
                        ))
                        : <span className='congestion-cell-mmsis-empty'>none recorded</span>}
                </div>
            )}

            {portId && (
                <CongestionHistogram
                    portId={portId}
                    range={range}
                    onShowTrack={setTrackMmsi}
                />
            )}

            <Dialog
                title='Confirm scan'
                modal
                open={!!scanEstimate}
                actions={[
                    <FlatButton
                        key='cancel'
                        label='Cancel'
                        disabled={startScan.isLoading}
                        onClick={() => setScanEstimate(null)}
                    />,
                    <RaisedButton
                        key='execute'
                        label={startScan.isLoading ? 'Starting…' : 'Execute'}
                        primary
                        disabled={startScan.isLoading}
                        onClick={() => startScan.mutate()}
                    />,
                ]}
            >
                {scanEstimate && (
                    <div>
                        <p>
                            This scan would process about{' '}
                            <b>{formatBytes(scanEstimate.totalBytesProcessed)}</b>
                            {' '}of BigQuery data
                            {scanEstimate.cacheHit ? ' (cached — likely not billed)' : ''}.
                            The estimate is free; nothing is scanned until you execute.
                        </p>
                        <p>
                            {scanEstimate.portName || 'Port'}
                            {scanEstimate.unLoCode ? ` (${scanEstimate.unLoCode})` : ''}
                            {' · '}
                            {fmtUtc(scanEstimate.dateFrom)} — {fmtUtc(scanEstimate.dateTo)}
                            {scanEstimate.mode === 'radius' && scanEstimate.radiusNm
                                ? ` · ${scanEstimate.radiusNm} nm`
                                : ''}
                        </p>
                    </div>
                )}
            </Dialog>

            <Snackbar
                open={!!message}
                message={message}
                autoHideDuration={4000}
                onRequestClose={() => setMessage('')}
            />
        </div>
    );
}



// WEBPACK FOOTER //
// ./app/components/Congestion/Congestion.jsx